import { useCallback, useEffect, useRef, useState } from "react"
import StartTimerButton from "../../time/StartTimerButton"
import { useTimeSummary } from "../../time/useTimeSummary"
import { decimalHours, humanDuration } from "../../time/duration"
import { useNavigate } from "react-router-dom"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import ChatIcon from "@mui/icons-material/ForumOutlined"
import AttachIcon from "@mui/icons-material/AttachFileOutlined"
import CopyIcon from "@mui/icons-material/ContentCopyOutlined"
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined"
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined"

import { taskApi } from "../../api"
import { useAuth } from "../../context/AuthContext"
import { usePermissions } from "../../permissions"
import { LoadingScreen } from "../../components/LoadingState"
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreOutlined"

import { formatDate, formatDateTime, relativeTime, titleCase } from "../../utils/format"
import UserAvatar from "../../components/UserAvatar"
import ActivityFeed from "../../components/ActivityFeed"
import InlineEdit from "../../components/InlineEdit"
import { MOTION, PRIORITY_COLORS, transition } from "../../theme"
import { uploadApi } from "../../api/uploads"
import { DropZone, FileChip, ImageAttachment } from "../../components/files"
import ProjectDetailDialog from "../projects/ProjectDetailDialog"
import SubtaskList from "./SubtaskList"
import CopyTaskDialog from "./CopyTaskDialog"

export default function TaskDetailDrawer({ taskId, statuses, onClose, onChanged }) {
  const { user } = useAuth()
  const { can, isBackOffice } = usePermissions()
  const navigate = useNavigate()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [comment, setComment] = useState("")
  const [internal, setInternal] = useState(false)
  const [busy, setBusy] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(null)
  const [saved, setSaved] = useState(false)
  const [openProjectId, setOpenProjectId] = useState(null)
  const [copying, setCopying] = useState(false)
  /* The checklist is refetching. It draws its own bar over the rows; the
     drawer stays exactly where it is. */
  const [stepsBusy, setStepsBusy] = useState(false)
  /* Per person rather than one total, because the question a manager opens a
     task with is "who has been on this", and the sum is one reduce away. */
  const time = useTimeSummary({ taskId: taskId ? Number(taskId) : null, groupBy: "user" })
  const fileInputRef = useRef(null)
  const savedTimer = useRef(null)

  /*
   * The task, with its comments, attachments and steps.
   *
   * ── Why it can refetch without the loading screen ───────────────────────
   *
   * The drawer renders a loading screen in place of its contents, which is
   * what you want when it opens on a task that is not here yet, and not what
   * you want when something inside it changed: ticking a step took the whole
   * panel away — the task, its history, the place the person was reading — to
   * redraw one line. Called with `quiet` it refetches underneath, and whatever
   * asked for it shows its own sign that something is in flight.
   */
  const load = useCallback(async (quiet = false) => {
    if (!taskId) return
    if (!quiet) setLoading(true)
    setError(null)
    try {
      setData(await taskApi.get(taskId))
    } catch (err) {
      setError(err.userMessage)
    } finally {
      if (!quiet) setLoading(false)
    }
  }, [taskId])

  useEffect(() => {
    setData(null)
    setComment("")
    setInternal(false)
    load()
  }, [load])

  /*
   * Status and priority are the only two controls in the app that save on
   * change, with no button and no acknowledgement. That is the drawer's design
   * and it is the right one — but it meant a mis-click was completely invisible:
   * the select showed the new value whether the request had landed, was still in
   * flight, or had failed and been silently reverted.
   *
   * So the save says so. A failure still goes to the alert at the top of the
   * drawer; this only ever confirms.
   */
  const patch = async (changes) => {
    setBusy(true)
    try {
      const result = await taskApi.update(taskId, changes)
      setData((current) => ({ ...current, task: result.task }))
      onChanged?.()
      setSaved(true)
      window.clearTimeout(savedTimer.current)
      savedTimer.current = window.setTimeout(() => setSaved(false), 2000)
    } catch (err) {
      setError(err.userMessage)
      setSaved(false)
    } finally {
      setBusy(false)
    }
  }

  // The drawer closes on every task, and a pending timer would set state on a
  // component that is gone.
  useEffect(() => () => window.clearTimeout(savedTimer.current), [])

  const uploadFiles = useCallback(
    async (files) => {
      if (!files?.length) return
      setUploadProgress(0)
      setError(null)
      try {
        const result = await uploadApi.toTask(taskId, files, { onProgress: setUploadProgress })
        // Per-file rejections travel with the successes: four good files still
        // attach, and the fifth says why it did not.
        if ((result.rejected || []).length > 0) {
          setError(result.rejected.map((r) => `${r.file_name}: ${r.message}`).join(" · "))
        }
        /* Quiet: the upload has its own progress bar, and the panel going
           blank on top of it says nothing the bar was not already saying. */
        await load(true)
        onChanged?.()
      } catch (err) {
        setError(err.userMessage)
      } finally {
        setUploadProgress(null)
      }
    },
    [taskId, load, onChanged],
  )

  const removeAttachment = async (attachment) => {
    setBusy(true)
    try {
      await uploadApi.removeTaskAttachment(taskId, attachment.id)
      await load(true)
      onChanged?.()
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setBusy(false)
    }
  }

  const postComment = async (event) => {
    event.preventDefault()
    if (!comment.trim()) return
    setBusy(true)
    try {
      await taskApi.comment(taskId, comment.trim(), internal)
      setComment("")
      /* Quiet: the composer is disabled while this runs, which is the whole
         signal needed. Replacing the thread with a loading screen to add one
         line to it loses the person's place in the conversation. */
      await load(true)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setBusy(false)
    }
  }

  const task = data?.task
  const isWatching = (data?.watchers || []).some((w) => Number(w.user_id) === Number(user?.id))

  return (
    <Drawer
      anchor="right"
      open={Boolean(taskId)}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: "100%", sm: 520 }, maxWidth: "100%" } } }}
    >
      {loading ? (
        <LoadingScreen minHeight="100vh" />
      ) : !task ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error || "This task is not available."}</Alert>
          <Button onClick={onClose} sx={{ mt: 2 }}>
            Close
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ p: 2.5, pb: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                  {task.task_code}
                </Typography>
                <Chip size="small" label={titleCase(task.type)} sx={{ height: 18, fontSize: 10 }} />
                {/* Only shown when it is OFF — a "client can see this" badge on
                    every task is noise; the exception is what matters. */}
                {isBackOffice && !task.visible_to_client ? (
                  <Chip size="small" color="warning" label="Hidden from client" sx={{ height: 18, fontSize: 10 }} />
                ) : null}
              </Stack>
              {/* Renamed where it is written. Fixing a typo should not mean
                  opening a form, and when it does the typo stays there. */}
              <InlineEdit
                value={task.title}
                variant="h3"
                // Still an h3: it is the only landmark on the panel.
                component="h3"
                disabled={!can("tasks", "update")}
                onSave={(title) => patch({ title })}
                sx={{ display: "block" }}
              />
            </Box>

            <StartTimerButton taskId={taskId} description={task.title} />

            <Tooltip title={isWatching ? "Stop following" : "Follow this task"}>
              <IconButton
                size="small"
                onClick={async () => {
                  await taskApi.watch(taskId, !isWatching).catch(() => {})
                  load(true)
                }}
              >
                {isWatching ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            {/* Copy this one somewhere else. Beside the close button rather
                than in the body: it acts on the whole task, which is what the
                header is for. */}
            {can("tasks", "create") ? (
              <Tooltip title="Copy this task to another project or client">
                <IconButton size="small" onClick={() => setCopying(true)} aria-label="Copy this task elsewhere">
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Divider />

          <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
            {error ? (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            ) : null}

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
              <TextField
                select
                size="small"
                label="Status"
                value={task.status_id}
                disabled={!can("tasks", "update") || busy}
                onChange={(e) => patch({ status_id: Number(e.target.value) })}
                sx={{ minWidth: 150 }}
              >
                {statuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Priority"
                value={task.priority}
                disabled={!can("tasks", "update") || busy}
                onChange={(e) => patch({ priority: e.target.value })}
                sx={{ minWidth: 130 }}
              >
                {["low", "medium", "high", "urgent"].map((p) => (
                  <MenuItem key={p} value={p}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: PRIORITY_COLORS[p] }} />
                      {titleCase(p)}
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>

              {/* The saved state, echoed. The dot is the priority the server
                  currently holds — not the one the select is showing — so the
                  two agreeing is itself the confirmation. */}
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ alignSelf: "flex-end", pb: 0.75 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: PRIORITY_COLORS[task.priority],
                    flexShrink: 0,
                    transition: transition("background-color", MOTION.base),
                  }}
                />
                <Typography
                  variant="caption"
                  color={saved ? "success.dark" : "text.secondary"}
                  sx={{ fontSize: 12, transition: transition("color", MOTION.base) }}
                >
                  {saved ? "Saved" : titleCase(task.priority)}
                </Typography>
              </Stack>
            </Stack>

            {/* A step arrived at from a notification is a row with no context.
                One line back up to what it is part of. */}
            {data.parent ? (
              <Chip
                size="small"
                variant="outlined"
                label={`Step of ${data.parent.task_code}: ${data.parent.title}`}
                onClick={() => navigate(`/tasks/${data.parent.id}`)}
                sx={{ mb: 1.5, maxWidth: "100%" }}
              />
            ) : null}

            {task.description ? (
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2.5 }}>
                {task.description}
              </Typography>
            ) : null}

            <Stack spacing={0.75} sx={{ mb: 2.5 }}>
              <Detail label="Client" value={task.client_label} />
              {/* The project a task belongs to is the most-asked question on
                  this panel, and it used to be a dead string. */}
              <Detail
                label="Project"
                value={task.project_name ? `${task.project_code} · ${task.project_name}` : "—"}
                onOpen={task.project_id ? () => setOpenProjectId(task.project_id) : null}
              />
              <Detail label="Assignee" value={task.assignee_name || "Unassigned"} />
              <Detail label="Reporter" value={task.reporter_name || "—"} />
              <Detail label="Due" value={formatDate(task.due_date)} />
              {task.completed_at ? <Detail label="Completed" value={formatDateTime(task.completed_at)} /> : null}
            </Stack>

            {/* Its steps, above the cost and below the facts: a task in
                progress is read for "how far along is this", and the answer is
                the checklist rather than anything in the panel's header. */}
            <Box sx={{ mb: 2.5 }}>
              <SubtaskList
                task={task}
                subtasks={data.subtasks || []}
                statuses={statuses}
                refreshing={stepsBusy}
                /* Returned rather than fired and forgotten: the checklist keeps
                   the tick it drew until the refreshed rows are actually here,
                   so the box never flicks back on its way to being right. */
                onChanged={async () => {
                  setStepsBusy(true)
                  try {
                    await load(true)
                    await onChanged?.()
                  } finally {
                    setStepsBusy(false)
                  }
                }}
                onOpen={(id) => navigate(`/tasks/${id}`)}
              />
            </Box>

            {/* What this task has actually cost so far.
                
                It sits above the attachments because it is the answer to the
                question people open a task to ask once it is in progress, and
                because a task with hours on it and no visible total is a task
                somebody re-times by reading the timesheet sideways. */}
            {time.mayView && !time.loading ? (
              <Box sx={{ mb: 2.5 }}>
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: "0.05em", flex: 1 }}>
                    {time.label.toUpperCase()}
                  </Typography>
                  {time.trackedSeconds > 0 ? (
                    <Typography variant="body2" fontWeight={650} sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {humanDuration(time.trackedSeconds)}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                        {decimalHours(time.trackedSeconds)} h
                      </Typography>
                    </Typography>
                  ) : null}
                </Stack>

                {time.trackedSeconds === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {time.error || "Nothing logged yet. Start the timer, or add an entry from your timesheet."}
                  </Typography>
                ) : (
                  <Stack spacing={0.25}>
                    {time.groups.map((group) => (
                      <Stack key={group.group_key} direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                          {group.group_label}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontVariantNumeric: "tabular-nums", flexShrink: 0 }}
                        >
                          {humanDuration(group.tracked_seconds)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>
            ) : null}

            {/* The other half of the chat↔work link: a task shows the
                conversations attached to it. */}
            {(data.chat_rooms || []).length > 0 ? (
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: "0.05em" }}>
                  CONVERSATIONS
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                  {data.chat_rooms.map((room) => (
                    <Button
                      key={room.id}
                      size="small"
                      startIcon={<ChatIcon fontSize="small" />}
                      onClick={() => navigate(`/chat/${room.id}`)}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      {room.topic || room.title || "Conversation"}
                    </Button>
                  ))}
                </Stack>
              </Box>
            ) : null}

            <Divider sx={{ mb: 2 }} />

            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: "0.05em", flex: 1 }}>
                ATTACHMENTS
              </Typography>
              {can("tasks", "manage_attachments") ? (
                <Button size="small" startIcon={<AttachIcon fontSize="small" />} onClick={() => fileInputRef.current?.click()}>
                  Attach
                </Button>
              ) : null}
            </Stack>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(event) => {
                uploadFiles([...event.target.files])
                event.target.value = ""
              }}
            />

            {uploadProgress !== null ? (
              <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 1.5, borderRadius: 1 }} />
            ) : null}

            <DropZone
              onFiles={uploadFiles}
              disabled={!can("tasks", "manage_attachments") || uploadProgress !== null}
              label="Drop files to attach"
            >
              <Box sx={{ mb: 2.5, minHeight: 44 }}>
                {(data.attachments || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    {can("tasks", "manage_attachments")
                      ? "Nothing attached yet. Drop a file here or use Attach."
                      : "Nothing attached."}
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {data.attachments.map((attachment) =>
                      attachment.inline ? (
                        <Stack key={attachment.id} spacing={0.5} alignItems="flex-start">
                          <ImageAttachment file={attachment} maxHeight={200} />
                          <FileChip
                            file={attachment}
                            dense
                            onRemove={can("tasks", "manage_attachments") && !busy ? removeAttachment : null}
                          />
                        </Stack>
                      ) : (
                        <FileChip
                          key={attachment.id}
                          file={attachment}
                          onRemove={can("tasks", "manage_attachments") && !busy ? removeAttachment : null}
                        />
                      ),
                    )}
                  </Stack>
                )}
              </Box>
            </DropZone>

            <Divider sx={{ mb: 2 }} />

            {/*
              * What has happened to this task, in sentences.
              *
              * The server has recorded every field change since the task was
              * raised and nothing ever showed it, so "why is this In Progress
              * when I marked it done" had no answer short of asking around.
              * Collapsed by default: it is the question people have
              * occasionally, and expanded it pushes the comments off the
              * screen.
              */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{ mb: 2, bgcolor: "transparent", "&::before": { display: "none" } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: "0.05em" }}>
                  ACTIVITY{(data.activity || []).length ? ` · ${data.activity.length}` : ""}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 1 }}>
                <ActivityFeed items={data.activity} emptyText="Nothing has been changed since it was raised." />
              </AccordionDetails>
            </Accordion>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: "0.05em" }}>
              COMMENTS
            </Typography>
            <Stack spacing={2} sx={{ mt: 1.5, mb: 2.5 }}>
              {(data.comments || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No comments yet.
                </Typography>
              ) : (
                data.comments.map((c) => (
                  <Stack key={c.id} direction="row" spacing={1.5}>
                    <UserAvatar
                      name={c.author_name}
                      email={c.author_email}
                      avatarKey={c.author_avatar}
                      size={28}
                      tooltip={false}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="baseline">
                        <Typography variant="body2" fontWeight={650}>
                          {c.author_name || c.author_email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {relativeTime(c.created_at)}
                        </Typography>
                        {c.is_internal ? (
                          <Chip size="small" color="warning" label="Internal" sx={{ height: 16, fontSize: 9 }} />
                        ) : null}
                      </Stack>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {c.body}
                      </Typography>
                    </Box>
                  </Stack>
                ))
              )}
            </Stack>
          </Box>

          {can("tasks", "comment") ? (
            <>
              <Divider />
              <Box component="form" onSubmit={postComment} sx={{ p: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  maxRows={4}
                  placeholder={internal ? "Internal note — the client will not see this" : "Add a comment…"}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  disabled={busy}
                />
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  {/* Back office only: a client marking their own comment
                      internal would hide it from the people it is addressed to. */}
                  {isBackOffice ? (
                    <Chip
                      size="small"
                      label="Internal note"
                      color={internal ? "warning" : "default"}
                      variant={internal ? "filled" : "outlined"}
                      onClick={() => setInternal((v) => !v)}
                    />
                  ) : null}
                  <Box sx={{ flex: 1 }} />
                  <Button type="submit" size="small" variant="contained" disabled={busy || !comment.trim()}>
                    Comment
                  </Button>
                </Stack>
              </Box>
            </>
          ) : null}
        </Box>
      )}
      {/* Over the drawer rather than instead of it: closing the project has to
          put somebody back on the task they were reading, not on the list they
          left two steps ago. */}
      <ProjectDetailDialog projectId={openProjectId} onClose={() => setOpenProjectId(null)} />
      <CopyTaskDialog
        task={copying ? data?.task : null}
        subtaskCount={(data?.subtasks || []).length}
        onClose={() => setCopying(false)}
        onCopied={(message) => {
          setCopying(false)
          setError(null)
          onChanged?.(message)
        }}
      />
    </Drawer>
  )
}

/** A labelled fact. With `onOpen` the value becomes the way into the thing it
 *  names, rather than a string you then have to go and find. */
function Detail({ label, value, onOpen }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>
        {label}
      </Typography>
      {onOpen && value && value !== "—" ? (
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={onOpen}
          sx={{ minWidth: 0, textAlign: "left", color: "primary.dark", fontWeight: 600 }}
        >
          {value}
        </Link>
      ) : (
        <Typography variant="body2" sx={{ minWidth: 0 }}>
          {value || "—"}
        </Typography>
      )}
    </Stack>
  )
}
